import React from 'react';
import { ArrowLeft, Check, Clock, Download, Mail, Copy, Building2, Calendar, Users } from 'lucide-react';
interface QuoteDetailProps {
  quoteId: string;
  onBack: () => void;
  quotes: any[];
}
export function QuoteDetail({
  quoteId,
  onBack,
  quotes
}: QuoteDetailProps) {
  const quote = quotes.find(q => q.id === quoteId);
  if (!quote) return <div data-id="element-490">Quote not found</div>;
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Contracted':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Held':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Sent':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Draft':
        return 'bg-stone-100 text-stone-700 border-stone-200';
      case 'Expired':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-491">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-492">
        <div className="flex items-center gap-4" data-id="element-493">
          <button onClick={onBack} className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-200 rounded-full transition-colors" data-id="element-494">
            <ArrowLeft className="w-5 h-5" data-id="element-495" />
          </button>
          <div data-id="element-496">
            <div className="flex items-center gap-3 mb-1" data-id="element-497">
              <h1 className="font-serif text-3xl text-stone-900" data-id="element-498">
                {quote.groupName}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(quote.status)}`} data-id="element-499">
                {quote.status}
              </span>
            </div>
            <p className="text-stone-500 font-mono text-sm" data-id="element-500">
              {quote.id} • Prepared for {quote.agency}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-501">
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-white border border-stone-200 hover:bg-stone-50 transition-colors shadow-sm" data-id="element-502">
            <Download className="w-4 h-4" data-id="element-503" />
            PDF
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 transition-colors shadow-sm" data-id="element-504">
            <Mail className="w-4 h-4" data-id="element-505" />
            Send to Agency
          </button>
        </div>
      </div>

      {/* Key Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-506">
        <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-start gap-4" data-id="element-507">
          <div className="p-3 bg-stone-100 rounded-md text-stone-600" data-id="element-508">
            <Building2 className="w-5 h-5" data-id="element-509" />
          </div>
          <div data-id="element-510">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-511">
              Property
            </p>
            <p className="font-medium text-stone-900" data-id="element-512">{quote.property}</p>
            <p className="text-sm text-stone-500" data-id="element-513">All-Inclusive Resort</p>
          </div>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-start gap-4" data-id="element-514">
          <div className="p-3 bg-stone-100 rounded-md text-stone-600" data-id="element-515">
            <Calendar className="w-5 h-5" data-id="element-516" />
          </div>
          <div data-id="element-517">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-518">
              Dates
            </p>
            <p className="font-medium text-stone-900" data-id="element-519">{quote.dates}</p>
            <p className="text-sm text-stone-500" data-id="element-520">4 Nights</p>
          </div>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-start gap-4" data-id="element-521">
          <div className="p-3 bg-stone-100 rounded-md text-stone-600" data-id="element-522">
            <Users className="w-5 h-5" data-id="element-523" />
          </div>
          <div data-id="element-524">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-525">
              Inventory
            </p>
            <p className="font-medium text-stone-900" data-id="element-526">{quote.rooms} Rooms</p>
            <p className="text-sm text-stone-500" data-id="element-527">~{quote.rooms * 2} Guests</p>
          </div>
        </div>
      </div>

      {/* Packages */}
      <div data-id="element-528">
        <h2 className="font-serif text-2xl text-stone-900 mb-6 mt-8" data-id="element-529">
          Auto-Generated Packages
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch" data-id="element-530">
          {/* Good */}
          <div className="bg-white border border-stone-200 shadow-sm flex flex-col" data-id="element-531">
            <div className="p-6 border-b border-stone-200 bg-stone-50/50" data-id="element-532">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-533">
                Option 1
              </p>
              <h3 className="font-serif text-2xl text-stone-900 mb-4" data-id="element-534">
                Standard Block
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-535">
                <span className="font-mono text-3xl text-stone-900" data-id="element-536">
                  ${(quote.value * 0.85).toLocaleString()}
                </span>
                <span className="text-stone-500 text-sm" data-id="element-537">total</span>
              </div>
              <p className="font-mono text-sm text-stone-500 mt-1" data-id="element-538">
                ${Math.round(quote.value * 0.85 / quote.rooms / 4)} / room /
                night
              </p>
            </div>
            <div className="p-6 flex-1" data-id="element-539">
              <ul className="space-y-4" data-id="element-540">
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-541">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-542" />
                  <span data-id="element-543">Run of House Rooms</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-544">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-545" />
                  <span data-id="element-546">Standard All-Inclusive Plan</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-547">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-548" />
                  <span data-id="element-549">1 comp room per 25 booked</span>
                </li>
              </ul>
            </div>
            <div className="p-6 pt-0 mt-auto" data-id="element-550">
              <button className="w-full py-2.5 border border-stone-300 text-stone-700 font-medium text-sm hover:bg-stone-50 transition-colors" data-id="element-551">
                Select Option
              </button>
            </div>
          </div>

          {/* Better (Recommended) */}
          <div className="bg-white border-2 border-red-600 shadow-md flex flex-col relative transform lg:-translate-y-2" data-id="element-552">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-600 text-white font-mono text-xs uppercase tracking-widest px-3 py-1" data-id="element-553">
              Recommended
            </div>
            <div className="p-6 border-b border-stone-200 bg-red-50/30" data-id="element-554">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-2" data-id="element-555">
                Option 2
              </p>
              <h3 className="font-serif text-2xl text-stone-900 mb-4" data-id="element-556">
                Premium Block
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-557">
                <span className="font-mono text-3xl text-stone-900" data-id="element-558">
                  ${quote.value.toLocaleString()}
                </span>
                <span className="text-stone-500 text-sm" data-id="element-559">total</span>
              </div>
              <p className="font-mono text-sm text-stone-500 mt-1" data-id="element-560">
                ${Math.round(quote.value / quote.rooms / 4)} / room / night
              </p>
            </div>
            <div className="p-6 flex-1" data-id="element-561">
              <ul className="space-y-4" data-id="element-562">
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-563">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-564" />
                  <span className="font-medium text-stone-900" data-id="element-565">
                    Ocean View Rooms Guaranteed
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-566">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-567" />
                  <span data-id="element-568">Standard All-Inclusive Plan</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-569">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-570" />
                  <span className="font-medium text-stone-900" data-id="element-571">
                    1 comp room per 20 booked
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-572">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-573" />
                  <span data-id="element-574">Private group check-in area</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-575">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-576" />
                  <span data-id="element-577">1 hour welcome cocktail reception</span>
                </li>
              </ul>
            </div>
            <div className="p-6 pt-0 mt-auto" data-id="element-578">
              <button className="w-full py-2.5 bg-red-600 text-white font-medium text-sm hover:bg-red-700 transition-colors shadow-sm" data-id="element-579">
                {quote.status === 'Held' ? 'Release Hold' : 'Place 72hr Hold'}
              </button>
            </div>
          </div>

          {/* Best */}
          <div className="bg-white border border-stone-200 shadow-sm flex flex-col" data-id="element-580">
            <div className="p-6 border-b border-stone-200 bg-stone-50/50" data-id="element-581">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-582">
                Option 3
              </p>
              <h3 className="font-serif text-2xl text-stone-900 mb-4" data-id="element-583">
                VIP Block
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-584">
                <span className="font-mono text-3xl text-stone-900" data-id="element-585">
                  ${(quote.value * 1.25).toLocaleString()}
                </span>
                <span className="text-stone-500 text-sm" data-id="element-586">total</span>
              </div>
              <p className="font-mono text-sm text-stone-500 mt-1" data-id="element-587">
                ${Math.round(quote.value * 1.25 / quote.rooms / 4)} / room /
                night
              </p>
            </div>
            <div className="p-6 flex-1" data-id="element-588">
              <ul className="space-y-4" data-id="element-589">
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-590">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-591" />
                  <span data-id="element-592">Ocean Front Suites Guaranteed</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-593">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-594" />
                  <span data-id="element-595">Premium All-Inclusive Plan</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-596">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-597" />
                  <span data-id="element-598">1 comp room per 15 booked</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-599">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-600" />
                  <span data-id="element-601">Private group check-in area</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-602">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-603" />
                  <span data-id="element-604">Private farewell dinner event</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-605">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-606" />
                  <span data-id="element-607">VIP amenities in all rooms</span>
                </li>
              </ul>
            </div>
            <div className="p-6 pt-0 mt-auto" data-id="element-608">
              <button className="w-full py-2.5 border border-stone-300 text-stone-700 font-medium text-sm hover:bg-stone-50 transition-colors" data-id="element-609">
                Select Option
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Trail */}
      <div className="mt-12 bg-white border border-stone-200 shadow-sm" data-id="element-610">
        <div className="p-6 border-b border-stone-200" data-id="element-611">
          <h2 className="font-serif text-xl text-stone-900" data-id="element-612">Audit Trail</h2>
        </div>
        <div className="p-6" data-id="element-613">
          <div className="space-y-6" data-id="element-614">
            <div className="flex gap-4" data-id="element-615">
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 text-stone-500 font-mono text-xs" data-id="element-616">
                JD
              </div>
              <div data-id="element-617">
                <p className="text-sm text-stone-900" data-id="element-618">
                  <span className="font-medium" data-id="element-619">John Doe</span> (Agency) viewed
                  proposal
                </p>
                <p className="text-xs text-stone-500 font-mono mt-1" data-id="element-620">
                  Today, 10:42 AM
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-621">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600 font-mono text-xs" data-id="element-622">
                BQ
              </div>
              <div data-id="element-623">
                <p className="text-sm text-stone-900" data-id="element-624">
                  <span className="font-medium" data-id="element-625">System</span> generated
                  Good/Better/Best packages
                </p>
                <p className="text-xs text-stone-500 font-mono mt-1" data-id="element-626">
                  Yesterday, 3:15 PM
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-627">
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 text-stone-500 font-mono text-xs" data-id="element-628">
                JD
              </div>
              <div data-id="element-629">
                <p className="text-sm text-stone-900" data-id="element-630">
                  <span className="font-medium" data-id="element-631">John Doe</span> (Agency)
                  submitted RFP via email
                </p>
                <p className="text-xs text-stone-500 font-mono mt-1" data-id="element-632">
                  Yesterday, 3:14 PM
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}